
import { AppNode, Edge } from '../types';

// Vite's `import.meta.env.DEV` flag without pulling in vite/client types.
// Used to surface JSON.parse failures during development without breaking
// production extraction (which is best-effort and tolerates bad data).
const IS_DEV =
    typeof import.meta !== 'undefined' && (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;

// Safety cap for the brace-matching scan in analyzeScriptReturns. If a return
// object literal is longer than this many characters we abandon the scan
// rather than risk a pathological loop on malformed input.
const MAX_RETURN_OBJECT_SCAN_CHARS = 2000;

const defaultForType = (type?: string): any => {
  switch (type) {
    case 'number':  return 123;
    case 'boolean': return true;
    case 'date':    return '2025-01-15';
    case 'time':    return '14:30:00';
    case 'datetime': return '2025-01-15T14:30:00';
    case 'email':   return 'user@example.com';
    case 'guid':
    case 'uuid':    return '00000000-0000-0000-0000-000000000000';
    case 'select':  return 'option1';
    case 'checkbox': return true;
    case 'textarea': return 'Sample text...';
    default:        return 'sample';
  }
};

export interface VariableDefinition {
  name: string;
  type: string; // 'string' | 'number' | 'boolean' | 'json' | 'array' | 'unknown'
  sourceNodeId: string;
  sourceNodeLabel: string;
  description?: string;
  example?: any;
}

const parseExampleResponse = (exampleResponse: any): any | undefined => {
  if (exampleResponse === null || exampleResponse === undefined) return undefined;
  if (typeof exampleResponse === 'string') {
    // Intentionally returns `undefined` on parse failure so callers can
    // fall back to their default shape via `?? fallback`. Without the DEV
    // log below, corrupt stored example responses would be silently
    // indistinguishable from an unset field. All call sites use the
    // value-or-fallback pattern, so we do NOT change the return shape.
    try {
      return JSON.parse(exampleResponse);
    } catch (e) {
      if (IS_DEV) {
        console.warn(
          '[variableExtraction] parseExampleResponse: stored exampleResponse is a non-empty string but failed JSON.parse; falling back to default example.',
          { snippet: exampleResponse.slice(0, 120), error: e }
        );
      }
      return undefined;
    }
  }
  return exampleResponse;
};

/**
 * Statically analyzes JavaScript code to find keys in return object literals.
 * Handles:
 * - return { key: val }
 * - return { "key": val }
 * - return { shorthand }
 * - Multiple return statements
 * - Basic nested objects (brace counting)
 */
const analyzeScriptReturns = (script: string): string[] => {
    if (!script || typeof script !== 'string') return [];
    const variables = new Set<string>();

    // 1. Remove comments to avoid false positives
    const cleanScript = script
        .replace(/\/\/.*$/gm, '') // Single line
        .replace(/\/\*[\s\S]*?\*\//g, ''); // Multi line

    // 2. Find locations of "return"
    const returnIndices = [...cleanScript.matchAll(/return\s*\{/g)].map(m => m.index!);

    for (const startIndex of returnIndices) {
        // We found "return {", now we need to find the matching closing "}"
        // The regex match places us at "r" of return.
        // We need to advance to the first "{"
        let braceLevel = 0;
        let objectContent = "";
        let foundStart = false;
        let i = startIndex;

        // Safety break
        while (i < cleanScript.length && i < startIndex + MAX_RETURN_OBJECT_SCAN_CHARS) {
            const char = cleanScript[i];
            
            if (char === '{') {
                if (!foundStart) foundStart = true;
                braceLevel++;
            } else if (char === '}') {
                braceLevel--;
            }

            if (foundStart) {
                objectContent += char;
                if (braceLevel === 0) break; // Found the closing brace for the return object
            }
            i++;
        }

        // 3. Extract keys from the object literal content
        // This looks for identifiers followed by ':' or shorthand identifiers followed by ',' or '}'
        // Regex explanation:
        // (?:[{,]\s*) -> Look for start of obj or comma separator
        // (?:["']?)   -> Optional quote start
        // ([\w$]+)    -> Capture Variable Name (Group 1)
        // (?:["']?)   -> Optional quote end
        // \s*         -> Whitespace
        // (?::|,|\})  -> Followed by colon (key:val), comma (shorthand), or end brace
        const keyMatches = objectContent.matchAll(/(?:[{,]\s*)(?:["']?)([\w$]+)(?:["']?)\s*(?::|,|\})/g);

        for (const match of keyMatches) {
            if (match[1]) {
                variables.add(match[1]);
            }
        }
    }

    return Array.from(variables);
};

/**
 * Traverses the graph backwards from the target node to find all upstream nodes
 * that contribute variables to the context.
 */
export const getAvailableVariables = (
  nodes: AppNode[],
  edges: Edge[],
  targetNodeId: string
): VariableDefinition[] => {
  const variables: VariableDefinition[] = [];
  
  // 0. Add System Variables (Always available)
  // We initialize the array with these to ensure it's never empty if properly called
  const systemVars = [
      { name: 'timestamp', type: 'number', sourceNodeId: 'system', sourceNodeLabel: 'System', description: 'sys.timestamp – Current execution timestamp (ms)', example: Date.now() },
      { name: 'processId', type: 'string', sourceNodeId: 'system', sourceNodeLabel: 'System', description: 'sys.processId – Process Instance ID', example: 'proc_123' },
      { name: 'nodeId', type: 'string', sourceNodeId: 'system', sourceNodeLabel: 'System', description: 'sys.nodeId – Current Node ID', example: targetNodeId },
  ];
  
  variables.push(...systemVars);

  // Safety check for empty graph
  if (!nodes || nodes.length === 0) {
      return variables;
  }

  const visited = new Set<string>();
  const queue = [targetNodeId];
  const ancestors = new Set<string>();

  // 1. BFS Backwards to find all ancestors
  // Build a reverse adjacency map for faster lookup
  const reverseAdjacency: Record<string, string[]> = {};
  edges.forEach(edge => {
    if (!reverseAdjacency[edge.target]) reverseAdjacency[edge.target] = [];
    reverseAdjacency[edge.target].push(edge.source);
  });

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    // Don't process the target node itself for variables (unless it's a loop, but we ignore self-refs for context)
    if (currentId !== targetNodeId) {
        ancestors.add(currentId);
    }

    const parents = reverseAdjacency[currentId] || [];
    parents.forEach(parentId => {
      if (!visited.has(parentId)) {
        visited.add(parentId);
        queue.push(parentId);
      }
    });
  }

  // 2. Iterate through all nodes, if they are ancestors, extract variables
  // We iterate through the original nodes array to maintain some semblance of definition order
  nodes.forEach(node => {
    if (!ancestors.has(node.id)) return;

    const extract = (name: string, type: string, desc?: string, example?: any) => {
        if (!name) return;

        // Avoid duplicates (simple override strategy)
        const existingIdx = variables.findIndex(v => v.name === name);
        const def: VariableDefinition = {
            name,
            type,
            sourceNodeId: node.id,
            sourceNodeLabel: node.data.label || node.id,
            description: desc,
            example
        };

        if (existingIdx >= 0) {
            variables[existingIdx] = def;
        } else {
            variables.push(def);
        }
    };

    // --- Extraction Logic based on Node Type ---

    // 1. Form Fields (Start Events & User Tasks)
    // Form fields are flat in the workflow context
    if (node.data.formSchema?.fields) {
        node.data.formSchema.fields.forEach((field: any) => {
            if (field.key) {
                extract(field.key, field.type || 'string', `Form field: ${field.label}`, defaultForType(field.type));
            }
        });
    }

    // 2. Start Events (Message/Signal Payloads)
    if (node.type === 'MessageStartEvent' && node.data.messagePayload) {
        // Flatten payload keys if possible, or just add the root
        extract('messagePayload', 'json', 'Incoming message body', node.data.messagePayload);
        // If the payload has known keys, add them
        try {
            const payload = typeof node.data.messagePayload === 'string' 
                ? JSON.parse(node.data.messagePayload) 
                : node.data.messagePayload;
                
            if (payload && typeof payload === 'object') {
                Object.keys(payload).forEach(key => {
                    extract(key, typeof payload[key], 'Message Payload Property', payload[key]);
                });
            }
        } catch (_e) { /* ignore parse errors */ }
    }

    // 3. Service Tasks (REST)
    if (node.type === 'ServiceTaskREST' && node.data.resultVariable) {
        const example = parseExampleResponse(node.data.exampleResponse) ?? { status: 200, data: {} };
        extract(node.data.resultVariable, 'json', 'API Response', example);
    }

    // 4. Database Tasks
    if (node.type === 'ServiceTaskDB' && node.data.resultVariable) {
        const example = parseExampleResponse(node.data.exampleResponse) ?? [{ id: 1 }];
        extract(node.data.resultVariable, 'array', 'Database Query Result', example);
    }

    // 5. LLM Tasks
    if (node.type === 'ServiceTaskLLM' && node.data.outputVariable) {
        extract(node.data.outputVariable, 'string', 'AI Generated Content', 'Sample AI response...');
    }

    // 6. MCP Tasks
    if (node.type === 'ServiceTaskMCP' && node.data.resultVariable) {
        const example = parseExampleResponse(node.data.exampleResponse) ?? { content: 'Sample data' };
        extract(node.data.resultVariable, 'any', 'MCP Operation Result', example);
    }

    // 7. Decision Tasks (Rules)
    if (node.type === 'ServiceTaskRules') {

        // Use result variable if set (wraps everything)
        if (node.data.resultVariable) {
            extract(node.data.resultVariable, 'object', 'Decision Result Wrapper', { score: 100 });
        }

        // Also extract individual outputs if known (from the UI selection state)
        if (node.data._outputSchema && Array.isArray(node.data._outputSchema)) {
            node.data._outputSchema.forEach((out: any) => {
                extract(out.name, out.type, `Decision Output (${node.data.decisionFile})`);
            });
        }
    }

    // 8. Call Activities
    if (node.type === 'CallActivity' && node.data.resultVariable) {
        extract(node.data.resultVariable, 'object', 'Call Activity Result', {});
    }

    // 9. Sub Processes
    if (node.type === 'SubProcess' && node.data.resultVariable) {
        extract(node.data.resultVariable, 'object', 'Sub Process Result', {});
    }

    // 10. Extraction Rules (Generic for Service Tasks)
    if (Array.isArray(node.data.extractionRules) && node.data.extractionRules.length > 0) {
        const rawResultVar = node.data.resultVariable || node.data.outputVariable;
        const resultVar = typeof rawResultVar === 'string' ? rawResultVar.trim() : '';

        if (resultVar) {
            // Nest rule outputs as properties of the result variable.
            // This overrides the generic REST/DB/MCP mock emitted above (extract() replaces by name).
            const nestedExample: Record<string, any> = {};
            node.data.extractionRules.forEach((rule: any) => {
                if (rule.targetVar) {
                    nestedExample[rule.targetVar] = 'Extracted Value';
                }
            });
            extract(resultVar, 'object', `Result of ${node.data.label ?? node.type ?? 'Service Task'}`, nestedExample);
        } else {
            // No result variable → rule targets are spread at root of context
            node.data.extractionRules.forEach((rule: any) => {
                if (rule.targetVar) {
                    extract(rule.targetVar, rule.type || 'auto', `Extracted from ${rule.source}`, 'Extracted Value');
                }
            });
        }
    }

    // 9. Script Tasks
    if (node.type === 'ScriptTask') {

        // 9a. Explicitly defined outputs (Manual Override)
        if (Array.isArray(node.data.outputVariables)) {
            node.data.outputVariables.forEach((varName: string) => {
                extract(varName, 'unknown', 'Explicitly defined script output');
            });
        }

        // 9b. Automatic Analysis (Return statements)
        if (node.data.script) {
            const detectedVars = analyzeScriptReturns(node.data.script);
            detectedVars.forEach(varName => {
                // Don't overwrite explicit ones, but add if new
                const existing = variables.find(v => v.name === varName && v.sourceNodeId === node.id);
                if (!existing) {
                    extract(varName, 'unknown', 'Auto-detected from script return');
                }
            });
        }
    }
  });

  return variables;
};
